import { USER_MEMORY_IMPORT_MAX_ITEMS } from "../constants/user-memory";
import type { IUserMemoryImportResult } from "../types/user-memory";

// NOTE: NSAI-6371 — a paste larger than one request, put back together.
//
// The endpoint caps a request at USER_MEMORY_IMPORT_MAX_ITEMS because the whole
// batch runs inside one transaction under one advisory lock, and that bound is
// deliberate. The user's side of the deal is "one action" for a list of any
// size, so the split is the client's job: chunk, send in order, and merge the
// answers back into the single result the UI reports.

export interface IChunkMemoryImportContentsProps {
  contents: string[];
  // NOTE: Defaults to the server's per-request cap; tests pass a small one.
  size?: number;
}

export interface IBuildUnsentMemoryImportResultProps {
  // NOTE: The lines that were never sent, in submitted order.
  contents: string[];
  // NOTE: The last answer that did arrive. Its usage is still the current one,
  // since nothing after it committed.
  lastResult: IUserMemoryImportResult;
}

export const chunkMemoryImportContents = ({
  contents,
  size = USER_MEMORY_IMPORT_MAX_ITEMS,
}: IChunkMemoryImportContentsProps): string[][] => {
  const chunks: string[][] = [];
  for (let start = 0; start < contents.length; start += size) {
    chunks.push(contents.slice(start, start + size));
  }
  return chunks;
};

// NOTE: Each response numbers its rows from 0 — they are indices into the chunk
// that was sent, not into the list the user submitted. Re-basing them onto the
// running offset is what keeps `items[i].index` meaningful to a caller that
// never saw the chunking, and it is what the line-number lookup in both result
// views depends on.
export const mergeMemoryImportResults = (
  results: IUserMemoryImportResult[],
): IUserMemoryImportResult => {
  const merged: IUserMemoryImportResult = {
    dryRun: results.every((result) => result.dryRun),
    created: 0,
    duplicates: 0,
    skipped: 0,
    items: [],
    explicitLimit: 0,
    explicitUsed: 0,
  };

  let offset = 0;
  for (const result of results) {
    merged.created += result.created;
    merged.duplicates += result.duplicates;
    merged.skipped += result.skipped;
    for (const item of result.items) {
      merged.items.push({ ...item, index: offset + item.index });
    }
    offset += result.items.length;
    // NOTE: The LAST chunk's usage is the current one — each chunk commits
    // before the next is sent, so earlier numbers are already stale.
    merged.explicitLimit = result.explicitLimit;
    merged.explicitUsed = result.explicitUsed;
  }

  return merged;
};

// NOTE: A request that fails after earlier chunks committed must not take
// their results with it: those memories are stored, and the user has to see
// which lines landed. The lines that were never sent are reported as skipped
// with `not_sent`, so the per-line report still covers the whole paste and
// says exactly which lines to import again.
export const buildUnsentMemoryImportResult = ({
  contents,
  lastResult,
}: IBuildUnsentMemoryImportResultProps): IUserMemoryImportResult => ({
  dryRun: lastResult.dryRun,
  created: 0,
  duplicates: 0,
  skipped: contents.length,
  items: contents.map((content, index) => ({
    index,
    content,
    status: "skipped",
    reason: "not_sent",
  })),
  explicitLimit: lastResult.explicitLimit,
  explicitUsed: lastResult.explicitUsed,
});
